import { Injectable, TemplateRef } from '@angular/core';
import { AppSettingsService, FeatureService, LocalStorageService, ModalOverlayService } from '@woolworthsnz/styleguide';
import { combineLatest, filter, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { EverydayRewardsFacade } from '../+state';
import { ContextResponse, EdrBalanceResponseV2, RewardMessage } from '@woolworthsnz/trader-api';
import { VoucherEarnMessageData } from '../models';

export const EDR_REDEEMABLE_VOUCHERS_VALUE_STORAGE_KEY = 'edr-redeemableVouchersValue';

@Injectable({
	providedIn: 'root',
})
export class EDRCelebratoryModalService {
	private redeemableRewardsVouchersCount$: Observable<number>;

	private rewardMessages: RewardMessage[] = [];
	private templateRef: TemplateRef<any> | undefined;

	constructor(
		private modalOverlayService: ModalOverlayService,
		private localStorage: LocalStorageService,
		private edrFacade: EverydayRewardsFacade,
		private appSettingsService: AppSettingsService,
		private featureService: FeatureService
	) {
		this.redeemableRewardsVouchersCount$ = this.edrFacade.edrBalance$.pipe(
			map((balance) => balance?.pointsAsVoucherCount ?? -1)
		);

		this.modalOverlayService.modalClosed$.subscribe(() => {
			if (this.rewardMessages.length && this.templateRef) {
				this.openRewardsModal(this.templateRef);
			}
		});
	}

	/**
	 * Conditionally opens edr celebratory modal
	 */
	public tryOpenCelebratoryModal(templateRef: TemplateRef<any>): void {
		// Since we might display multiple reward messages, we need to store the templateRef to be able to open the modal again
		this.templateRef = templateRef;

		const rewardMessages$ = this.edrFacade.fetchedRewardMessages$.pipe(
			filter(Boolean),
			// Should not show any celebratory modals in embedded app
			filter(() => !this.appSettingsService.getSetting('isEmbeddedInApp')),
			switchMap(() => this.edrFacade.rewardMessages$),
			tap((rewardMessages) => {
				this.rewardMessages = rewardMessages;

				if (rewardMessages.length) {
					this.localStorage.setItem(EDR_REDEEMABLE_VOUCHERS_VALUE_STORAGE_KEY, '999999');
				}
			}),
			take(1)
		);

		rewardMessages$
			.pipe(
				switchMap(() =>
					combineLatest([
						this.redeemableRewardsVouchersCount$,
						of(parseInt(this.localStorage.getItem(EDR_REDEEMABLE_VOUCHERS_VALUE_STORAGE_KEY) || '0', 10)),
						this.edrFacade.fetchedEdrBalance$.pipe(
							filter(Boolean),
							switchMap(() =>
								this.edrFacade.redemptionPreference$.pipe(
									map(
										(preference) =>
											preference === EdrBalanceResponseV2.RedemptionPreferenceEnum.SpendNow
									)
								)
							)
						),
					]).pipe(
						tap(([redeemableVouchersCount, storedVouchersValue, isSpendNow]) => {
							// Only save if this is not the default value from the constructor, otherwise that default value overwrites any previously stored value
							if (redeemableVouchersCount !== -1) {
								this.localStorage.setItem(
									EDR_REDEEMABLE_VOUCHERS_VALUE_STORAGE_KEY,
									redeemableVouchersCount.toString()
								);
							}

							// If there are no voucher earn messages, we then use the old logic to display the voucher earned modal ONLY if the redemption preference is SpendNow
							if (
								this.rewardMessages.length === 0 && // We don't want to execute the legacy logic if there are any reward messages
								redeemableVouchersCount > storedVouchersValue && // Only show the modal if the redeemable vouchers value has increased
								isSpendNow
							) {
								const voucherEarnedData: VoucherEarnMessageData = {
									currentVouchersValue: redeemableVouchersCount * 15,
									currentVouchersCount: redeemableVouchersCount,
								};

								const voucherEarnedRewerdMessage: RewardMessage = {
									messageId: '-1',
									type: RewardMessage.TypeEnum.VoucherEarn,
									createdDateTime: new Date().toISOString(),
									data: voucherEarnedData,
								};

								const rewardMessages = [...this.rewardMessages];
								rewardMessages.push(voucherEarnedRewerdMessage);
								this.rewardMessages = rewardMessages;
							}
						}),
						take(1)
					)
				)
			)
			.subscribe(() => {
				this.openRewardsModal(templateRef);
			});
	}

	private openRewardsModal(templateRef: TemplateRef<any>): void {
		if (!this.rewardMessages.length) {
			return;
		}

		const rewardMessages = [...this.rewardMessages];
		const rewardMessage = rewardMessages.shift();
		this.rewardMessages = rewardMessages;

		if (rewardMessage) {
			// Do not show any Aves related notifications until the Aves feature flag is enabled
			if (
				(rewardMessage.type === RewardMessage.TypeEnum.AirnzBurn ||
					rewardMessage.type === RewardMessage.TypeEnum.QantasBurn) &&
				!this.featureService.isFeatureEnabled(ContextResponse.EnabledFeaturesEnum.EnableEdrAves)
			) {
				return;
			}

			this.modalOverlayService.setState({ hasBackdrop: false, data: rewardMessage });
			this.modalOverlayService.open({ templateRef });

			// Delete the reward message
			// TODO: Remove check for messageId === '-1' once we remove the old logic to look at local storage for voucher earned
			if (rewardMessage.messageId !== '-1') {
				this.edrFacade.deleteRewardMessage(rewardMessage.messageId);
			}
		}
	}
}
